import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AxiosError } from "axios";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FieldError, fieldErrorAria, fieldErrorId } from "@/components/auth/FieldError";
import { submitEnquiry } from "@/api/enquiry";

type EnquiryForm = {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
};

const EMPTY: EnquiryForm = { name: "", company: "", email: "", phone: "", message: "" };

/**
 * Contact-page enquiry form. Replaces the published staff phone numbers — the
 * team reaches out using the submitted details. On success the form clears.
 */
export function EnquiryForm() {
  const { t } = useTranslation("contact");

  // Validation messages come from the active locale, so the schema is memoised
  // on `t` and rebuilt when the language changes.
  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t("errors.required")).max(120, t("errors.tooLong")),
        company: z.string().trim().min(1, t("errors.required")).max(200, t("errors.tooLong")),
        email: z.string().trim().email(t("errors.invalidEmail")).max(254),
        phone: z.string().trim().min(7, t("errors.phoneTooShort")).max(30, t("errors.tooLong")),
        message: z.string().trim().min(1, t("errors.messageRequired")).max(3000, t("errors.tooLong"))
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<EnquiryForm>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY
  });

  const mutation = useMutation({
    mutationFn: (input: EnquiryForm) => submitEnquiry(input),
    onSuccess: (data) => {
      toast.success(data?.message || t("toast.success"));
      reset(EMPTY);
    },
    onError: (err: AxiosError<{ error?: string; message?: string }>) => {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(t("toast.rateLimited"));
      } else {
        toast.error(err.response?.data?.message || t("toast.error"));
      }
    }
  });

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-5" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="enq-name">{t("form.name")}</Label>
          <Input id="enq-name" autoComplete="name" {...register("name")} {...fieldErrorAria("enq-name", errors.name)} />
          <FieldError id={fieldErrorId("enq-name")} error={errors.name} />
        </div>
        <div>
          <Label htmlFor="enq-company">{t("form.company")}</Label>
          <Input id="enq-company" autoComplete="organization" {...register("company")} {...fieldErrorAria("enq-company", errors.company)} />
          <FieldError id={fieldErrorId("enq-company")} error={errors.company} />
        </div>
        <div>
          <Label htmlFor="enq-email">{t("form.email")}</Label>
          <Input id="enq-email" type="email" autoComplete="email" {...register("email")} {...fieldErrorAria("enq-email", errors.email)} />
          <FieldError id={fieldErrorId("enq-email")} error={errors.email} />
        </div>
        <div>
          <Label htmlFor="enq-phone">{t("form.phone")}</Label>
          <Input id="enq-phone" type="tel" autoComplete="tel" placeholder={t("form.phonePlaceholder")} {...register("phone")} {...fieldErrorAria("enq-phone", errors.phone)} />
          <FieldError id={fieldErrorId("enq-phone")} error={errors.phone} />
        </div>
      </div>

      <div>
        <Label htmlFor="enq-message">{t("form.enquiry")}</Label>
        <Textarea
          id="enq-message"
          placeholder={t("form.enquiryPlaceholder")}
          {...register("message")}
          {...fieldErrorAria("enq-message", errors.message)}
        />
        <FieldError id={fieldErrorId("enq-message")} error={errors.message} />
      </div>

      <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={mutation.isPending}>
        {mutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("form.submitting")}</> : t("form.submit")}
      </Button>
    </form>
  );
}
